/**
 * Command Center (no ML) — wizard state and everything derived from it.
 *
 * A partner (Adrsta as a third party, or another partner) runs the maths and
 * shares a design deck, then results decks while the flight runs (mid-flight reads). Decks are uploaded, stored and read
 * by the backend (deck_parser.py); every number here comes from those decks.
 */

import { DeckFields, Engine } from "./api";
import { CardInfo, CheckSummary, DECISION_LABEL, FLOW_V, Tone, addDays, fmtDay, money, onTrack, parseDay, quarterOf } from "./common";

export interface DeckState {
  name: string;
  adv: string;
  partner: "adrsta" | "other";
  kpi: "rev" | "cnt";
  channel: string;
  /** The question this test answers, typed by the user (older tests hold a preset key). */
  decision: string;
  startDate: string;
  /** Max weekly spend in test markets, in $K. */
  budget: number;
  /** Longest flight the advertiser allows (sent to the partner as a limit). */
  maxWeeks?: number;
  change: "no" | "yes";
  /** What else changes in the test markets, when change is "yes". */
  changeNote?: string;
  sent: boolean;
  /** 2 once the design deck is stored (read by the backend guardrails). */
  dUp: number;
  bar: number;
  cntBar: number;
  onWin: "scale" | "roll" | "keep";
  phase: number;
  rUp: number;
  finalDecision: string;
  saved: boolean;
  stage: number;
  maxStage: number;
  flightWeeks?: number;
  /** Stage-numbering version (common.migrateFlow). */
  flowV?: number;
  result?: { lift: number; roas: number | null; met: boolean; weeks: number; source: string; channel: string; season: string };
}

export const DECK_DEFAULT: DeckState = {
  name: "", adv: "", partner: "adrsta", kpi: "rev", channel: "CTV", decision: "", startDate: "", budget: 60, maxWeeks: 6, change: "no",
  sent: false, dUp: 0, bar: 2.0, cntBar: 4, onWin: "scale", phase: 0, rUp: 0, finalDecision: "", saved: false,
  stage: 0, maxStage: 0, flightWeeks: 6, flowV: FLOW_V,
};

/** Same steps as Command Center + ML (ML_STAGES): results decks arrive mid-flight, in In flight. */
export const DECK_STAGES = ["Plan", "Design", "Test plan", "In flight", "Decide"];

/** Readable names for the fields deck_parser reports as missing. */
export const DECK_FIELD_LABEL: Record<string, string> = {
  method: "method", weeks: "flight length", mde: "detectable lift (MDE)", weekly_budget_k: "weekly budget", markets: "test markets",
  lift: "lift", confidence: "confidence", spend_k: "actual spend", iroas_or_incremental: "iROAS or incremental result",
};

const slugify = (v: string) => v.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "");

export function computeDeck(s: DeckState, engine: Engine = {}) {
  const rev = s.kpi === "rev";
  const partner = s.partner === "adrsta" ? "Adrsta" : "the partner";
  const partnerCap = s.partner === "adrsta" ? "Adrsta" : "Partner";
  const advName = s.adv.trim() || "Advertiser";
  const slug = s.partner === "adrsta" ? "adrsta" : "partner";
  const designDeck = engine.decks?.design || null;
  const resultsDeck = engine.decks?.results || null;
  const dz: DeckFields = designDeck?.parsed?.fields || {};
  const rz: DeckFields = resultsDeck?.parsed?.fields || {};
  const weeks = dz.weeks ?? rz.weeks ?? s.flightWeeks ?? 6;
  const start = parseDay(s.startDate);
  const end = start ? addDays(start, weeks * 7) : null;
  const flight = start && end ? `${fmtDay(start)} – ${fmtDay(end)} · ${weeks} wk` : `${weeks} wk`;
  const barCond = rev ? `iROAS ≥ ${s.bar.toFixed(1)}x` : `lift ≥ ${s.cntBar}%`;

  // Design (from the design deck)
  const method = dz.method ?? rz.method;
  const split = dz.split ?? rz.split;
  const methodText = method === "RCT" ? `Randomized controlled trial${split ? ` (${split}/${100 - split})` : ""}` : method || "—";
  const marketsText = dz.test_markets?.length ? dz.test_markets.join(", ")
    : dz.n_test ? `${dz.n_test} markets${method === "RCT" ? " (random)" : ""}` : "—";
  const controlText = dz.control || (dz.n_control ? `${dz.n_control} markets` : "—");
  const wkK = dz.weekly_budget_k;
  const totK = dz.total_budget_k ?? (wkK != null ? wkK * weeks : undefined);
  const spendText = wkK != null ? `${money(totK ?? 0)} · ${money(wkK)} / wk` : "—";
  const spendLong = wkK != null ? `${money(totK ?? 0)} total · ${money(wkK)} / week` : "—";
  const mdeText = dz.mde != null ? `${dz.mde.toFixed(1)}%` : "—";

  // Results (from the results deck)
  const lift = rz.lift, iroas = rz.iroas, conf = rz.confidence;
  const hasResult = !!resultsDeck && lift != null;
  const sig = conf != null && conf >= 80;
  const met = hasResult && sig && (rev ? (iroas ?? 0) >= s.bar - 1e-9 : (lift ?? 0) >= s.cntBar);
  const liftText = lift != null ? `${lift >= 0 ? "+" : ""}${lift.toFixed(1)}%` : "—";
  const incText = rz.incremental != null ? (rev ? (rz.incremental >= 1e6 ? `$${(rz.incremental / 1e6).toFixed(2)}M` : `$${Math.round(rz.incremental / 1e3)}K`)
    : `+${Math.round(rz.incremental).toLocaleString()}`) : "—";

  return {
    rev, partner, partnerCap, advName, slug, weeks, start, end, flight,
    // Every results deck is a read; one sent after the flight ended is final.
    reads: engine.deck_reads || [], final: !!resultsDeck && resultsDeck.final !== false,
    tracking: hasResult && onTrack(rev, iroas, lift, s.bar, s.cntBar),
    barCond, met, sig, hasResult,
    dz, rz, method, methodText, marketsText, controlText, spendText, spendLong, mdeText, totK,
    lift, liftText, iroas, conf, incText,
    spendKText: rz.spend_k != null ? money(rz.spend_k) : "—",
    overBudget: wkK != null && wkK > s.budget,
    designDeck, resultsDeck,
    designParse: designDeck?.parsed || null,
    resultsParse: resultsDeck?.parsed || null,
    designFile: `${slug}_design_${slugify(advName)}.pdf`,
    resFile: `${slug}_results_${slugify(advName)}.pdf`,
    kpiShort: rev ? "KPI: revenue" : "KPI: count",
    effLabel: rev ? "iROAS" : "Cost per incremental",
    effVal: rev ? (iroas != null ? iroas.toFixed(2) + "x" : "—")
      : rz.spend_k != null && rz.incremental ? "$" + ((rz.spend_k * 1000) / rz.incremental).toFixed(2) : "—",
  };
}

export type DeckDerived = ReturnType<typeof computeDeck>;

/** Same statuses as Command Center + ML (mlStatus). */
export function deckStatus(s: DeckState): { key: string; label: string; tone: Tone } {
  switch (s.maxStage) {
    case 0: return { key: "draft", label: "Draft", tone: "violet" };
    case 1: return { key: "planning", label: "Planning", tone: "violet" };
    case 2: return { key: "designing", label: "Designing", tone: "violet" };
    case 3: return { key: "plan_ready", label: "Plan ready", tone: "blue" };
    case 4:
      if (s.phase >= 2) return { key: "ended", label: "Flight ended", tone: "violet" };
      if (s.phase >= 1) return { key: "in_flight", label: "Running", tone: "amber" };
      return { key: "scheduled", label: "Scheduled", tone: "amber" };
    default: return s.saved ? { key: "closed", label: "Closed", tone: "green" } : { key: "deciding", label: "Deciding", tone: "green" };
  }
}

export function deckCard(s: DeckState, engine: Engine, checks?: CheckSummary): CardInfo {
  const d = computeDeck(s, engine);
  const st = deckStatus(s);
  const stageN = Math.max(1, s.maxStage);
  const chips = [`${stageN} · ${DECK_STAGES[stageN - 1]}`, `Measured by ${d.partnerCap === "Partner" ? "partner" : d.partnerCap}`, `${fmtDay(d.start)} · ${d.weeks} wk`];
  const base = { segDone: Math.max(0, stageN - 1), segCur: true, chips, badge: st.label, tone: st.tone };

  if (s.saved) {
    const r = typeof s.result?.lift === "number" ? s.result : undefined;
    return {
      ...base, badge: (r?.met ?? d.met) ? "Closed · bar met" : "Closed · bar missed", tone: (r?.met ?? d.met) ? "green" : "amber",
      bucket: "closed", segDone: 5, segCur: false, next: "",
      result: {
        lift: d.liftText, eff: d.effVal, effLabel: d.effLabel,
        bar: d.rev ? s.bar.toFixed(1) + "x" : s.cntBar + "%", met: r?.met ?? d.met,
        decision: DECISION_LABEL[s.finalDecision] || "recorded",
      },
    };
  }
  if (checks && checks.block > 0) {
    return { ...base, badge: `${checks.block} blocker${checks.block > 1 ? "s" : ""}`, tone: "red", bucket: "action",
      next: checks.first_block ? `Blocker: ${checks.first_block}` : "Next: clear the blocker →", nextWarn: true };
  }
  switch (s.maxStage) {
    case 0:
    case 1: return { ...base, bucket: "active", next: "Next: finish the plan →" };
    case 2:
      if (s.dUp === 2) return { ...base, bucket: "action", next: "Next: review the test plan →" };
      return { ...base, bucket: "action", next: `Next: upload the design deck from ${d.partner} →` };
    case 3: return { ...base, bucket: "action", next: "Next: approve the plan and schedule →" };
    case 4: {
      const latest = d.hasResult ? `latest read ${d.liftText} lift` : "no results deck yet";
      if (s.phase >= 2) {
        return d.hasResult && d.final
          ? { ...base, bucket: "action", next: "Next: record the decision →" }
          : { ...base, bucket: "action", next: `Next: upload the final results deck from ${d.partner} (${latest}) →`, nextWarn: true };
      }
      return { ...base, bucket: "active",
        next: s.phase >= 1 ? `Ends ${fmtDay(d.end)} · ${latest}${d.hasResult ? (d.tracking ? " (on track)" : " (behind the bar)") : ""}` : `Starts ${fmtDay(d.start)} · ${d.partner} reads mid-flight` };
    }
    default: return { ...base, bucket: "action", next: "Next: record the decision →" };
  }
}

export function deckResult(s: DeckState, engine: Engine): NonNullable<DeckState["result"]> {
  const d = computeDeck(s, engine);
  return {
    lift: d.lift ?? 0, roas: d.rev ? d.iroas ?? null : null, met: d.met, weeks: d.weeks,
    source: `Results deck · ${d.partnerCap}`, channel: s.channel || "CTV", season: quarterOf(d.start),
  };
}
